import React, { useState, useEffect } from 'react';
import { btnPrimary, FERTILITY_DEMO_SECTION_BORDER, introCardKeyline, introHeadlineSerifDeep, demoChapterStackColumnStyle } from '../designSystem';
import { NovaUnifiedInlineAnalysis, NovaUnifiedMediumHeader } from '../components/NovaUnified';

const thisWeekDates = ['2025-03-22', '2025-03-24', '2025-03-26'];

const pillBase = {
  fontSize: 11,
  fontWeight: 600,
  padding: '4px 12px',
  borderRadius: 20,
  border: '0.5px solid var(--border)',
  background: 'var(--white)',
  color: 'var(--muted)',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const pillActive = {
  ...pillBase,
  background: 'var(--pink)',
  color: 'white',
  borderColor: 'var(--pink)',
};

const tabBtn = (active) => ({
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 13,
  padding: '7px 14px',
  color: active ? 'var(--pink)' : 'var(--muted)',
  fontWeight: active ? 600 : 400,
  borderBottom: active ? '2px solid var(--pink)' : '2px solid transparent',
  marginBottom: -1,
});

function Badge({ children, bg, color, size = 10 }) {
  return (
    <span
      style={{
        fontSize: size,
        fontWeight: 700,
        padding: '1px 6px',
        borderRadius: 6,
        background: bg,
        color,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

function CategoryBadge({ label, bg, color }) {
  return (
    <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 6, background: bg, color, whiteSpace: 'nowrap' }}>{label}</span>
  );
}

function SandBadge({ label }) {
  return <CategoryBadge label={label} bg="var(--sand-2)" color="var(--muted)" />;
}

export default function FertilityDemoEvents({ navigate, omitHeader, demoChapter = 'fertility' }) {
  const sectionBorder = demoChapter === 'fertility' ? FERTILITY_DEMO_SECTION_BORDER : '0.5px solid var(--border, #E8E0D8)';
  const [activeTab, setActiveTab] = useState('nearby');
  const [nearbyFilter, setNearbyFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [eventName, setEventName] = useState('');
  const [category, setCategory] = useState('support');
  const [customCategory, setCustomCategory] = useState('');
  const [imageIndex, setImageIndex] = useState(0);
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [format, setFormat] = useState('inperson');
  const [meetingLink, setMeetingLink] = useState('');
  const [cityArea, setCityArea] = useState('Columbus, OH');
  const [description, setDescription] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [repeatMode, setRepeatMode] = useState('biweekly');
  const [endsAfter, setEndsAfter] = useState('3');
  const [visibility, setVisibility] = useState('public');
  const [inviteSearch, setInviteSearch] = useState('');
  const [inviteEmails, setInviteEmails] = useState('');

  useEffect(() => {
    if (category === 'custom') return;
    const byCategory = {
      support: 0,
      informational: 3,
      walk: 2,
      virtualcoffee: 1,
      workshop: 8,
      loss: 4,
      partner: 5,
    };
    const idx = byCategory[category];
    if (idx != null) setImageIndex(idx);
  }, [category]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const IMAGE_OPTIONS = [
    { bg: '#f4c0d1', emoji: '🤝' },
    { bg: '#eeedfe', emoji: '☕' },
    { bg: '#e1f5ee', emoji: '🌿' },
    { bg: '#fef0eb', emoji: '📋' },
    { bg: '#fbeaf0', emoji: '🎗️' },
    { bg: '#f4c0d1', emoji: '💙' },
    { bg: '#e1f5ee', emoji: '🌸' },
    { bg: '#eeedfe', emoji: '🤲' },
    { bg: '#fef0eb', emoji: '📚' },
    { upload: true },
  ];

  const CATEGORY_OPTIONS = [
    { id: 'support', label: 'Support circle', dot: '#d4537e' },
    { id: 'informational', label: 'Informational', dot: '#7f77dd' },
    { id: 'walk', label: 'Walk & talk', dot: '#1d9e75' },
    { id: 'virtualcoffee', label: 'Virtual coffee', dot: '#ad3f1a' },
    { id: 'workshop', label: 'Workshop', dot: '#ba7517' },
    { id: 'loss', label: 'Loss & grief', dot: '#888780' },
    { id: 'partner', label: 'Partner welcome', dot: 'rgba(212,83,126,0.5)' },
    { id: 'custom', label: '+ Custom', custom: true },
  ];

  const inputBase = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '9px 12px',
    borderRadius: 8,
    border: '0.5px solid var(--border)',
    fontSize: 13,
    fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
    background: 'var(--white)',
    color: 'var(--text)',
  };

  return (
    <div
      className="chapter-fertility"
      style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif", width: '100%', boxSizing: 'border-box', ...demoChapterStackColumnStyle }}
    >
      {!omitHeader && (
        <div
          style={{
            background: 'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))',
            ...introCardKeyline,
            padding: '22px 26px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#993556', marginBottom: 4 }}>
                Events · Fertility · Columbus, OH
              </div>
              <div style={{ ...introHeadlineSerifDeep, marginBottom: 3 }}>Events near you</div>
              <div style={{ fontSize: 12, color: 'rgba(60,30,45,0.5)' }}>
                Fertility, TTC, pregnancy & women&apos;s health events in Ohio
              </div>
            </div>
            <button
              type="button"
              onClick={openModal}
              style={{ ...btnPrimary, padding: '7px 18px', fontSize: 11, flexShrink: 0 }}
            >
              + Create an event
            </button>
          </div>
          <NovaUnifiedInlineAnalysis compact style={{ marginTop: 0 }}>
            <NovaUnifiedMediumHeader contextPill="Guidance" />
            <p style={{ fontSize: 12, color: '#4b1528', lineHeight: 1.5, margin: 0 }}>
              Nova found <strong style={{ fontWeight: 600 }}>{thisWeekDates.length} events</strong> this week that match your IVF stage —{' '}
              <strong style={{ fontWeight: 600 }}>IVF Support Circle</strong> is tonight in Columbus.
            </p>
          </NovaUnifiedInlineAnalysis>
        </div>
      )}

      {/* Main card — Events near me */}
      <div
        style={{
          background: 'var(--card, #FFFDF9)',
          border: sectionBorder,
          borderRadius: 14,
          padding: '16px 18px',
        }}
      >
        <h2 className="section-title">Events near me</h2>
        <p className="section-descriptor">Local meetups and support circles · Columbus, OH</p>

        <div
          style={{
            display: 'flex',
            borderBottom: '0.5px solid var(--border)',
            marginTop: 10,
            marginBottom: 14,
          }}
        >
          <button type="button" style={tabBtn(activeTab === 'nearby')} onClick={() => setActiveTab('nearby')}>
            Nearby
          </button>
          <button type="button" style={tabBtn(activeTab === 'myevents')} onClick={() => setActiveTab('myevents')}>
            My events
          </button>
          <button type="button" style={tabBtn(activeTab === 'hosting')} onClick={() => setActiveTab('hosting')}>
            Hosting
          </button>
        </div>

        {activeTab === 'nearby' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button type="button" onClick={() => setNearbyFilter('all')} style={nearbyFilter === 'all' ? pillActive : pillBase}>
                  All
                </button>
                <button type="button" onClick={() => setNearbyFilter('in-person')} style={nearbyFilter === 'in-person' ? pillActive : pillBase}>
                  In person
                </button>
                <button type="button" onClick={() => setNearbyFilter('virtual')} style={nearbyFilter === 'virtual' ? pillActive : pillBase}>
                  Virtual
                </button>
              </div>
              <button
                type="button"
                onClick={openModal}
                style={{
                  background: 'var(--pink, #d4537e)',
                  color: 'white',
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '5px 16px',
                  borderRadius: 20,
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                + Host an event
              </button>
            </div>

            {/* On OrivonAI */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>On OrivonAI</span>
              <Badge bg="var(--pink-l)" color="var(--pink-d)" size={9}>
                Platform events
              </Badge>
            </div>

            {/* Featured */}
            <div
              style={{
                background: 'var(--pink-l)',
                border: '0.5px solid var(--pink-m, #f4c0d1)',
                borderRadius: 10,
                padding: '12px 14px',
                marginBottom: 10,
              }}
            >
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: 54,
                    height: 54,
                    borderRadius: 8,
                    background: '#f4c0d1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 26,
                    flexShrink: 0,
                  }}
                >
                  🤝
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 4 }}>
                    <Badge bg="var(--pink-l)" color="var(--pink-d)" size={9}>
                      Tonight
                    </Badge>
                    <SandBadge label="Support circle" />
                    <SandBadge label="In person" />
                    <Badge bg="var(--pink-l)" color="var(--pink-d)" size={9}>
                      OrivonAI
                    </Badge>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>IVF Support Circle — Columbus</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>6:30 PM · Dublin Rec Center · Free</div>
                  <div style={{ fontSize: 10, color: 'var(--pink-d)', fontWeight: 600, marginTop: 3 }}>12 women attending · 3 from your chapter</div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/signup')}
                  style={{
                    background: 'var(--pink)',
                    color: 'white',
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '6px 14px',
                    borderRadius: 8,
                    border: 'none',
                    alignSelf: 'center',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    flexShrink: 0,
                  }}
                >
                  Join →
                </button>
              </div>
            </div>

            {/* Regular rows */}
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '10px 0',
                borderBottom: '0.5px solid var(--sand-2, #ede8df)',
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: '#eeedfe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  flexShrink: 0,
                }}
              >
                ☕
              </div>
              <div style={{ width: 32, textAlign: 'center', flexShrink: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--pink-d)', textTransform: 'uppercase' }}>Mar</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>24</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 4 }}>
                  <CategoryBadge label="Virtual coffee" bg="var(--purple-l)" color="var(--purple-d)" />
                  <SandBadge label="Virtual" />
                  <Badge bg="var(--pink-l)" color="var(--pink-d)" size={9}>
                    OrivonAI
                  </Badge>
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Two-Week Wait Virtual Coffee</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>9:00 AM · Zoom · Free · Recurring every 2 weeks</div>
              </div>
              <button
                type="button"
                onClick={() => navigate('/signup')}
                style={{
                  background: 'var(--pink-l)',
                  color: 'var(--pink-d)',
                  border: '1px solid var(--pink-m)',
                  borderRadius: 8,
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '5px 12px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  alignSelf: 'center',
                  flexShrink: 0,
                }}
              >
                Join
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: '0.5px solid var(--sand-2, #ede8df)' }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: '#e1f5ee',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  flexShrink: 0,
                }}
              >
                🌿
              </div>
              <div style={{ width: 32, textAlign: 'center', flexShrink: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--pink-d)', textTransform: 'uppercase' }}>Mar</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>26</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 4 }}>
                  <CategoryBadge label="Walk & talk" bg="var(--sage-l)" color="var(--sage-d)" />
                  <SandBadge label="In person" />
                  <Badge bg="var(--pink-l)" color="var(--pink-d)" size={9}>
                    OrivonAI
                  </Badge>
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Fertility Walk & Talk — Goodale Park</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>8:00 AM · Columbus · Free · 4 going</div>
              </div>
              <button
                type="button"
                onClick={() => navigate('/signup')}
                style={{
                  background: 'var(--pink-l)',
                  color: 'var(--pink-d)',
                  border: '1px solid var(--pink-m)',
                  borderRadius: 8,
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '5px 12px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  alignSelf: 'center',
                  flexShrink: 0,
                }}
              >
                Join
              </button>
            </div>

            {/* Also in your area */}
            <div
              style={{
                marginTop: 14,
                paddingTop: 14,
                borderTop: '0.5px solid var(--sand-2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 10,
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>Also in your area</span>
              <Badge bg="#f1efe8" color="#444441" size={9}>
                External · links out
              </Badge>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: '0.5px solid var(--sand-2, #ede8df)' }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: '#f1efe8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  flexShrink: 0,
                }}
              >
                📋
              </div>
              <div style={{ width: 32, textAlign: 'center', flexShrink: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--pink-d)', textTransform: 'uppercase' }}>Mar</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>27</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 4 }}>
                  <CategoryBadge label="Informational" bg="var(--amber-l)" color="var(--amber)" />
                  <SandBadge label="Virtual" />
                  <Badge bg="#f1efe8" color="#444441" size={9}>
                    RESOLVE
                  </Badge>
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Understanding IVF Costs — National Webinar</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>7:00 PM · Free · resolve.org</div>
              </div>
              <button
                type="button"
                onClick={() => navigate('/signup')}
                style={{
                  background: 'var(--white)',
                  color: 'var(--muted)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '5px 12px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  alignSelf: 'center',
                  flexShrink: 0,
                }}
              >
                View →
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: '0.5px solid var(--sand-2, #ede8df)' }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: '#e1f5ee',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  flexShrink: 0,
                }}
              >
                🏥
              </div>
              <div style={{ width: 32, textAlign: 'center', flexShrink: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--pink-d)', textTransform: 'uppercase' }}>Mar</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>29</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 4 }}>
                  <CategoryBadge label="Open house" bg="var(--sage-l)" color="var(--sage-d)" />
                  <SandBadge label="In person" />
                  <Badge bg="#e1f5ee" color="#0f6e56" size={9}>
                    Pinnacle Fertility
                  </Badge>
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Fertility Q&A Evening — Pinnacle Columbus</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>6:00 PM · Westerville, OH · Free</div>
              </div>
              <button
                type="button"
                onClick={() => navigate('/signup')}
                style={{
                  background: 'var(--white)',
                  color: 'var(--muted)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '5px 12px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  alignSelf: 'center',
                  flexShrink: 0,
                }}
              >
                View →
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0' }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: '#fbeaf0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  flexShrink: 0,
                }}
              >
                🎗️
              </div>
              <div style={{ width: 32, textAlign: 'center', flexShrink: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--pink-d)', textTransform: 'uppercase' }}>Apr</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>2</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 4 }}>
                  <CategoryBadge label="Support circle" bg="var(--pink-l)" color="var(--pink-d)" />
                  <SandBadge label="In person" />
                  <Badge bg="#f1efe8" color="#444441" size={9}>
                    Eventbrite
                  </Badge>
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Pregnancy After Loss Support Group</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>6:30 PM · Cincinnati, OH · Free</div>
              </div>
              <button
                type="button"
                onClick={() => navigate('/signup')}
                style={{
                  background: 'var(--white)',
                  color: 'var(--muted)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '5px 12px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  alignSelf: 'center',
                  flexShrink: 0,
                }}
              >
                View →
              </button>
            </div>
          </>
        )}

        {activeTab === 'myevents' && (
          <>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 10 }}>Upcoming · 2</div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: '0.5px solid var(--sand-2)' }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: '#f4c0d1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  flexShrink: 0,
                }}
              >
                🤝
              </div>
              <div style={{ width: 32, textAlign: 'center', flexShrink: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--pink-d)', textTransform: 'uppercase' }}>Mar</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>29</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 4 }}>
                  <CategoryBadge label="Support circle" bg="var(--pink-l)" color="var(--pink-d)" />
                  <SandBadge label="In person" />
                  <CategoryBadge label="Going" bg="var(--sage-l)" color="var(--sage-d)" />
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>IVF Support Circle — Columbus</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Saturday 10:00 AM · Columbus, OH</div>
              </div>
              <button
                type="button"
                onClick={() => navigate('/signup')}
                style={{ fontSize: 11, color: 'var(--pink)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', alignSelf: 'center', flexShrink: 0 }}
              >
                Add to calendar
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: '0.5px solid var(--sand-2)' }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: '#eeedfe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  flexShrink: 0,
                }}
              >
                ☕
              </div>
              <div style={{ width: 32, textAlign: 'center', flexShrink: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--pink-d)', textTransform: 'uppercase' }}>Apr</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>3</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 4 }}>
                  <CategoryBadge label="Virtual coffee" bg="var(--purple-l)" color="var(--purple-d)" />
                  <SandBadge label="Virtual" />
                  <CategoryBadge label="Going" bg="var(--sage-l)" color="var(--sage-d)" />
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Two-Week Wait Virtual Coffee</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Thursday 9:00 AM · Zoom</div>
              </div>
              <button
                type="button"
                onClick={() => navigate('/signup')}
                style={{ fontSize: 11, color: 'var(--pink)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', alignSelf: 'center', flexShrink: 0 }}
              >
                Add to calendar
              </button>
            </div>

            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 16, paddingTop: 16, borderTop: '0.5px solid var(--sand-2)', marginBottom: 10 }}>Past · 3</div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', opacity: 0.6 }}>
              <div style={{ width: 32, textAlign: 'center', flexShrink: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--pink-d)', textTransform: 'uppercase' }}>Mar</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>8</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>IVF Grief Circle</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Attended · In person · Columbus</div>
              </div>
              <span style={{ fontSize: 11, color: 'var(--muted)', alignSelf: 'center' }}>Left a comment</span>
            </div>
          </>
        )}

        {activeTab === 'hosting' && (
          <>
            <div
              style={{
                background: 'var(--pink-l)',
                border: '0.5px solid var(--pink-m)',
                borderRadius: 10,
                padding: 16,
                marginBottom: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: 'var(--pink)',
                  color: 'white',
                  fontSize: 22,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontWeight: 300,
                  lineHeight: 1,
                }}
              >
                +
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--pink-d)' }}>Host an event</div>
                <div style={{ fontSize: 11, color: '#4b1528', marginTop: 3, lineHeight: 1.45 }}>
                  Support circle, walk & talk, virtual coffee — create a meetup for women in your area.
                </div>
              </div>
              <button
                type="button"
                onClick={openModal}
                style={{
                  background: 'var(--pink)',
                  color: 'white',
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '7px 16px',
                  borderRadius: 8,
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  flexShrink: 0,
                }}
              >
                Create →
              </button>
            </div>

            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 10 }}>Your events · 2 upcoming</div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: '0.5px solid var(--sand-2)' }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: '#f4c0d1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  flexShrink: 0,
                }}
              >
                🤝
              </div>
              <div style={{ width: 32, textAlign: 'center', flexShrink: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--pink-d)', textTransform: 'uppercase' }}>Apr</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>5</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 4 }}>
                  <CategoryBadge label="Support circle" bg="var(--pink-l)" color="var(--pink-d)" />
                  <SandBadge label="In person" />
                  <CategoryBadge label="Public" bg="var(--sage-l)" color="var(--sage-d)" />
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Westerville IVF Coffee Circle</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>10:00 AM · Columbus · 7 going</div>
              </div>
              <button type="button" onClick={openModal} style={{ fontSize: 11, color: 'var(--pink)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', alignSelf: 'center' }}>
                Edit →
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0' }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: '#eeedfe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  flexShrink: 0,
                }}
              >
                ☕
              </div>
              <div style={{ width: 32, textAlign: 'center', flexShrink: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--pink-d)', textTransform: 'uppercase' }}>Apr</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>19</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 4 }}>
                  <CategoryBadge label="Virtual coffee" bg="var(--purple-l)" color="var(--purple-d)" />
                  <SandBadge label="Virtual" />
                  <CategoryBadge label="Public" bg="var(--sage-l)" color="var(--sage-d)" />
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Westerville IVF Coffee Circle</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>10:00 AM · Zoom · 3 going · Recurring</div>
              </div>
              <button type="button" onClick={openModal} style={{ fontSize: 11, color: 'var(--pink)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', alignSelf: 'center' }}>
                Edit →
              </button>
            </div>
          </>
        )}
      </div>

      {isModalOpen ? (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
          onClick={closeModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-event-title"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--card, #FFFDF9)',
              borderRadius: 16,
              padding: 24,
              width: 560,
              maxWidth: '100%',
              maxHeight: '85vh',
              overflowY: 'auto',
              boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <h2 id="create-event-title" className="section-title">
                  Create an event
                </h2>
                <p className="section-descriptor" style={{ marginBottom: 0 }}>
                  About 2 minutes to set up.
                </p>
              </div>
              <button type="button" aria-label="Close" onClick={closeModal} style={{ fontSize: 16, color: 'var(--muted)', cursor: 'pointer', background: 'none', border: 'none', padding: 4 }}>
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div className="card-label" style={{ marginBottom: 6 }}>
                  Event name
                </div>
                <input
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  placeholder="e.g. Westerville IVF Coffee Circle"
                  style={inputBase}
                />
              </div>

              <div>
                <div className="card-label" style={{ marginBottom: 6 }}>
                  Category
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {CATEGORY_OPTIONS.map((c) => {
                    const sel = category === c.id;
                    if (c.custom) {
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setCategory('custom')}
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            padding: '5px 11px',
                            borderRadius: 20,
                            border: sel ? '0.5px solid var(--pink)' : '0.5px dashed var(--pink-m)',
                            background: sel ? 'var(--pink-l)' : 'var(--white)',
                            color: sel ? 'var(--pink-d)' : 'var(--pink-m)',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                          }}
                        >
                          {c.label}
                        </button>
                      );
                    }
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          setCategory(c.id);
                        }}
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          padding: '5px 11px',
                          borderRadius: 20,
                          border: sel ? '0.5px solid var(--pink)' : '0.5px solid var(--border)',
                          background: sel ? 'var(--pink-l)' : 'var(--white)',
                          color: sel ? 'var(--pink-d)' : 'var(--muted)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5,
                          fontFamily: 'inherit',
                        }}
                      >
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
                        {c.label}
                      </button>
                    );
                  })}
                </div>
                {category === 'custom' ? (
                  <input
                    value={customCategory}
                    onChange={(e) => setCustomCategory(e.target.value)}
                    placeholder="What would you call it?"
                    style={{ ...inputBase, marginTop: 8 }}
                  />
                ) : null}
              </div>

              <div>
                <div className="card-label" style={{ marginBottom: 6 }}>
                  Image
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
                  {IMAGE_OPTIONS.map((opt, i) =>
                    opt.upload ? (
                      <button
                        key="upload"
                        type="button"
                        onClick={() => setImageIndex(9)}
                        style={{
                          height: 44,
                          borderRadius: 8,
                          cursor: 'pointer',
                          background: 'var(--sand-2)',
                          border: imageIndex === 9 ? '1.5px solid var(--pink)' : '0.5px dashed var(--border)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 10,
                          fontWeight: 600,
                          color: 'var(--muted)',
                          fontFamily: 'inherit',
                        }}
                      >
                        Upload
                      </button>
                    ) : (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setImageIndex(i)}
                        style={{
                          height: 44,
                          borderRadius: 8,
                          cursor: 'pointer',
                          background: opt.bg,
                          border: imageIndex === i ? '1.5px solid var(--pink)' : '1.5px solid transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 20,
                          fontFamily: 'inherit',
                          padding: 0,
                        }}
                      >
                        {opt.emoji}
                      </button>
                    ),
                  )}
                </div>
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Auto-selected by category. Change or upload your own.</div>
              </div>

              <div>
                <div className="card-label" style={{ marginBottom: 6 }}>
                  Date & time
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <div className="card-label" style={{ marginBottom: 4, fontSize: 10 }}>
                      Date
                    </div>
                    <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} style={inputBase} />
                  </div>
                  <div>
                    <div className="card-label" style={{ marginBottom: 4, fontSize: 10 }}>
                      Start time
                    </div>
                    <input type="time" value={eventTime} onChange={(e) => setEventTime(e.target.value)} style={inputBase} />
                  </div>
                </div>
              </div>

              <div>
                <div className="card-label" style={{ marginBottom: 6 }}>
                  Format
                </div>
                <div style={{ display: 'flex', gap: 5 }}>
                  <button
                    type="button"
                    onClick={() => setFormat('inperson')}
                    style={{
                      flex: 1,
                      textAlign: 'center',
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '6px 0',
                      borderRadius: 8,
                      border: format === 'inperson' ? '0.5px solid var(--pink)' : '0.5px solid var(--border)',
                      background: format === 'inperson' ? 'var(--pink)' : 'var(--white)',
                      color: format === 'inperson' ? 'white' : 'var(--muted)',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    In person
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormat('virtual')}
                    style={{
                      flex: 1,
                      textAlign: 'center',
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '6px 0',
                      borderRadius: 8,
                      border: format === 'virtual' ? '0.5px solid var(--pink)' : '0.5px solid var(--border)',
                      background: format === 'virtual' ? 'var(--pink)' : 'var(--white)',
                      color: format === 'virtual' ? 'white' : 'var(--muted)',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    Virtual
                  </button>
                </div>
              </div>

              {format === 'virtual' ? (
                <div style={{ background: '#eeedfe', border: '0.5px solid #afa9ec', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--purple-d)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Meeting link</div>
                  <input
                    value={meetingLink}
                    onChange={(e) => setMeetingLink(e.target.value)}
                    placeholder="Paste your Zoom, Google Meet, or Teams link..."
                    style={{
                      border: '0.5px solid #afa9ec',
                      borderRadius: 8,
                      padding: '7px 10px',
                      fontSize: 12,
                      width: '100%',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit',
                    }}
                  />
                  <div style={{ fontSize: 10, color: 'var(--purple-d)', marginTop: 5, opacity: 0.8 }}>Shared only with confirmed attendees — not shown publicly.</div>
                </div>
              ) : (
                <div>
                  <div className="card-label" style={{ marginBottom: 6 }}>
                    City / area
                  </div>
                  <input value={cityArea} onChange={(e) => setCityArea(e.target.value)} style={inputBase} />
                </div>
              )}

              <div>
                <div className="card-label" style={{ marginBottom: 6 }}>
                  What to expect <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
                </div>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Peer-led, no medical advice — just real talk with women who get it..."
                  rows={2}
                  style={{ ...inputBase, resize: 'none', lineHeight: 1.5 }}
                />
              </div>

              <div>
                <div className="card-label" style={{ marginBottom: 6 }}>
                  Recurring event
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={isRecurring} onChange={(e) => setIsRecurring(e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--pink)' }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>This is a recurring event</span>
                </label>
                {isRecurring ? (
                  <div style={{ background: 'var(--sand)', borderRadius: 8, padding: '10px 12px', marginTop: 8 }}>
                    <div style={{ fontSize: 10, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6 }}>Repeats</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {['Weekly', 'Every 2 weeks', 'Monthly', 'Custom'].map((label, idx) => {
                        const keys = ['weekly', 'biweekly', 'monthly', 'custom'];
                        const k = keys[idx];
                        const sel = repeatMode === k;
                        return (
                          <button
                            key={k}
                            type="button"
                            onClick={() => setRepeatMode(k)}
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              padding: '5px 10px',
                              borderRadius: 8,
                              border: sel ? '0.5px solid var(--pink)' : '0.5px solid var(--border)',
                              background: sel ? 'var(--pink)' : 'var(--white)',
                              color: sel ? 'white' : 'var(--muted)',
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12, color: 'var(--muted)' }}>
                      <span>Ends after</span>
                      <select
                        value={endsAfter}
                        onChange={(e) => setEndsAfter(e.target.value)}
                        style={{ border: '0.5px solid var(--border)', borderRadius: 6, padding: '4px 8px', fontSize: 11, fontFamily: 'inherit', background: 'var(--white)' }}
                      >
                        <option value="3">3 occurrences</option>
                        <option value="6">6 occurrences</option>
                        <option value="12">12 occurrences</option>
                        <option value="date">A specific date</option>
                        <option value="never">Never</option>
                      </select>
                      <span style={{ fontSize: 10, color: 'var(--muted)' }}>· Next: Apr 7, Apr 21, May 5...</span>
                    </div>
                  </div>
                ) : null}
              </div>

              <div>
                <div className="card-label" style={{ marginBottom: 6 }}>
                  Visibility
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => setVisibility('public')}
                    style={{
                      flex: 1,
                      border: visibility === 'public' ? '0.5px solid var(--pink)' : '0.5px solid var(--border)',
                      borderRadius: 8,
                      padding: '9px 11px',
                      cursor: 'pointer',
                      background: visibility === 'public' ? 'var(--pink-l)' : 'transparent',
                      textAlign: 'left',
                      fontFamily: 'inherit',
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600, color: visibility === 'public' ? 'var(--pink-d)' : 'var(--text)' }}>Public</div>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2, lineHeight: 1.35 }}>
                      Listed on Events Near Me · shareable link · Nova may surface to matched women
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setVisibility('private')}
                    style={{
                      flex: 1,
                      border: visibility === 'private' ? '0.5px solid var(--pink)' : '0.5px solid var(--border)',
                      borderRadius: 8,
                      padding: '9px 11px',
                      cursor: 'pointer',
                      background: visibility === 'private' ? 'var(--pink-l)' : 'transparent',
                      textAlign: 'left',
                      fontFamily: 'inherit',
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600, color: visibility === 'private' ? 'var(--pink-d)' : 'var(--text)' }}>Private</div>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2, lineHeight: 1.35 }}>Invite only · not listed · only invited people can see it</div>
                  </button>
                </div>
              </div>

              <div>
                <div className="card-label" style={{ marginBottom: 6 }}>
                  Invite from OrivonAI
                </div>
                <div style={{ background: 'var(--sand)', borderRadius: 8, padding: '8px 10px', marginBottom: 6, fontSize: 11, color: 'var(--muted)', lineHeight: 1.5 }}>
                  Nova will automatically notify matched women nearby. You can also personally invite connected women.
                </div>
                <input
                  value={inviteSearch}
                  onChange={(e) => setInviteSearch(e.target.value)}
                  placeholder="Search by name — e.g. Jamie, Noelle..."
                  style={inputBase}
                />
              </div>

              <div>
                <div className="card-label" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <span>Invite by email</span>
                  <span style={{ background: 'var(--pink-l)', color: 'var(--pink-d)', fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 5 }}>Growth</span>
                </div>
                <textarea
                  value={inviteEmails}
                  onChange={(e) => setInviteEmails(e.target.value)}
                  placeholder="Paste email addresses separated by commas — non-OrivonAI users get a branded invite and land on a public event page with a signup prompt..."
                  rows={2}
                  style={{ ...inputBase, resize: 'none', lineHeight: 1.5 }}
                />
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3 }}>
                  Non-OrivonAI users can view the event and RSVP — they&apos;ll be prompted to create a free account.
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, paddingTop: 6 }}>
                <button
                  type="button"
                  onClick={() => {
                    closeModal();
                    navigate('/signup');
                  }}
                  style={{
                    flex: 2,
                    background: 'var(--pink)',
                    color: 'white',
                    fontSize: 13,
                    fontWeight: 600,
                    padding: 10,
                    borderRadius: 10,
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Create event
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  style={{
                    flex: 1,
                    background: 'var(--white)',
                    color: 'var(--muted)',
                    fontSize: 12,
                    fontWeight: 600,
                    padding: 10,
                    borderRadius: 10,
                    border: '0.5px solid var(--border)',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Save draft
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
